'use client';
import { ChatKeyboard } from './ChatKeyboard';
import { Fragment, useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { SharedConversationStatus } from './SharedConversationStatus';
import { usePersistentState } from '../../lib/usePersistentState';
import { useTranslation } from '../../lib/i18n';
import { useMobileViewport } from '../../lib/useMobile';
import { useToast } from '../../components/ui/Toast';
import type { BrainCard, DashRecap } from '../../lib/types';
import { submittedPlanOf, turnsFromHistory, type ChatTurn } from '../../lib/transcript';
import { ElowenApiError } from '../../lib/elowenClient';
import { ConversationSearch } from './ConversationSearch';
import { AskQuestionCard } from './AskQuestionCard';
import { StatsModal } from './StatsModal';
import { HelpModal } from './HelpModal';
import { PlanCard } from './PlanCard';
import { useBrainChat, useBrainChatStatus, useBrainChatTranscript } from './BrainChatProvider';
import { LoadingState, Spinner } from '../../components/ui/states';
import { ChatStart } from './ChatStart';
import { isBackgroundProcessCardId } from '../../lib/processScope';
import { uiZoom } from '../../lib/uiZoom';
import { projectVoiceCallTurns } from '../../lib/voiceCallPresentation';
import { cardItemsAddressable } from '../../lib/railTasks';
import type { PluginChatPendingInput } from 'elowen-plugin-ui-kit';
import { PluginChatDockHost, useRailTakeovers } from '../../lib/pluginChatUi';
import { chatDockFloatTop } from './chatDockFloat';
import { AssistantRun, Message, type ReplyQuoteFor } from './ChatTranscript';
import { isUnseenPosition } from './brainChatHistory';
import type { ReactionControl } from './MessageReactions';
import { quoteSnippet } from './ReplyQuote';
import { useReplyQuotes } from './replyQuotes';
import { useMe } from '../../lib/queries';
import { isConversationOnlyTurn, runOutcome, transcriptBlocks } from './workRuns';
import { TranscriptAmbientExtras } from './ChatCards';
import { ChatConversationBar } from './ChatConversationBar';
import { ChatFooterDock } from './ChatFooterDock';
import { Button } from '../../components/ui/Button';
import { createScrollSettleGate, type ScrollSettleGate } from './scrollSettle';

const STATUSLINE_VALUES = ['shown', 'hidden'] as const;
/** The plugin views that belong to the conversation rather than to a turn (a running Project desktop, a
 *  live browser session). Drawn from live state, so they do not depend on which turns the transcript has
 *  loaded. The surface hands them the live narration and the waiting-prompt notice directly: it re-renders
 *  on every token anyway, and no transcript row reads either value. */
function ProjectChatDock({ narration, pendingInput }: { narration: string; pendingInput: PluginChatPendingInput | null }) {
  const { pluginSessionId } = useBrainChat();
  const { telemetry } = useBrainChatStatus();
  return <PluginChatDockHost project={telemetry.projectRef ?? null} sessionId={pluginSessionId}
    narration={narration} pendingInput={pendingInput} />;
}
/** How long a jumped-to message keeps its focus ring. */
const JUMP_RING_MS = 1200;
const JUMP_RING = ['rounded-md', 'ring-2', 'ring-ring'];
const NO_IDS: ReadonlySet<string> = new Set();
/** Segment wrappers use display:contents; scrolling and visibility must measure the same rendered box. */
function messageBox(target: HTMLElement): HTMLElement {
  return getComputedStyle(target).display === 'contents' && target.firstElementChild instanceof HTMLElement ? target.firstElementChild : target;
}
/** How close to the bottom still counts as sitting ON the newest turn. */
const BOTTOM_GAP = 80;
/** How far above the scrollport, in scrollport heights, the oldest loaded turn may be before the next
 *  older page is requested. Pages keep coming while the band is not filled, so a page that folds into a
 *  collapsed run without adding height is followed by the next one until there is content to scroll into.
 *  Three screens give a touch fling room to run while its page waits for the scroller to settle. */
const OLDER_BAND_SCREENS = 3;
/** The gap a docked desktop tile keeps above the composer. It mirrors the `.75rem` term in the dock rule of
 *  `desktop.css`: the stylesheet decides where a tile sits, so clearance measured against it has to be
 *  written from the same offset. */
const DOCK_TILE_GAP_REM = 0.75;
/** The gap between two stacked tiles. */
const DOCK_STACK_GAP_REM = 0.5;
/** One rem in real pixels. The tile's offset is authored in rem and the app rescales the root font size
 *  (`--ui-scale`), so a hardcoded 12 would drift away from the tile at every zoom except 100%. */
const remPx = (): number => parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
/** The last turn when nothing with content follows it: only the empty modal extras and the chat dock's
 *  tiles, which the dock floats out of flow, stand between it and the transcript end. */
function lastTurnBeforeEnd(end: HTMLElement): HTMLElement | null {
  let node = end.previousElementSibling;
  if (!(node instanceof HTMLElement) || !node.matches('.chat-extras:empty')) return null;
  node = node.previousElementSibling;
  while (node instanceof HTMLElement && node.matches('[data-chat-dock]')) node = node.previousElementSibling;
  return node instanceof HTMLElement && node.matches('.chat-turn') ? node : null;
}
/** The desktop tiles floating above the composer right now, in document order.
 *
 *  A tile announces itself with the host-owned `data-chat-dock` marker (`DesktopPreview`, placement `chat`),
 *  so nothing here has to guess a plugin descendant by height or know a plugin's class. It counts only while
 *  it really is a floating layer: below the phone breakpoint and on the compact dock the stylesheet leaves it
 *  in the flow, where it takes room like any other block and reserves nothing; an expanded tile is invisible
 *  (its surface is raised by the shared modal) and its reservation is released.
 *
 *  Why not `:scope > .desktop-preview`: a tile is NOT a child of the surface. It is rendered deep in the
 *  transcript, inside a turn, and only its `position: absolute` lifts it out to the surface, which is its
 *  containing block. A child selector matches nothing at all. */
const floatingDockTiles = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>('[data-chat-dock]:not([data-expanded="true"])'))
    .filter((tile) => getComputedStyle(tile).position === 'absolute' && tile.getBoundingClientRect().height > 0);
/** The presentational brain chat surface, driven entirely by the shared controller (BrainChatProvider)
 *  read from context. The provider consumes the shared transcript fold; this root composes memoized
 *  renderers and owns DOM refs, scrolling and dock geometry, not network or session state. Unmounting
 *  it (Chat↔Terminál toggle, route
 *  change) never tears down the stream, draft or transcript. The conversation list / search / rename /
 *  export / delete are the shared switcher the provider mounts, which this surface only opens — both
 *  variants through the same `openHistory`, so the dock and /chat can never disagree about which list is
 *  the list. `variant` selects the dock (compact) look or the wide /chat (full) look; `onOpenTelemetry`
 *  opens the telemetry drawer, passed only where the rail cannot be a column (a phone). `openSessionId`
 *  opens one stored conversation instead of the default one (the /chat?session= link; see
 *  `openConversationLink` for who writes there), and
 *  `openMessageId` then jumps to one message of it (the `&message=` of a mention alert).
 *  `recapSeed` is the /chat route's server-fetched recap, handed to the new-conversation start so its
 *  greeting is right on the first frame. */
export function BrainChatSurface({ variant = 'compact', onOpenTelemetry, telemetryShown, openSessionId = null, openMessageId = null, recapSeed = null }: { variant?: 'compact' | 'full'; onOpenTelemetry?: () => void; telemetryShown?: boolean; openSessionId?: string | null; openMessageId?: string | null; recapSeed?: DashRecap | null }) {
  const { t } = useTranslation();
  const takeovers = useRailTakeovers();
  const { toast } = useToast();
  const {
    statsOpen, setStatsOpen,
    helpTab, closeHelp,
    onAnswer, reloadAsk, activeSessionId, models,
    ensureAttached, openConversationLink, loadOlder, showThoughts,
    registerSurface, notifyActivityVisibility, reactToMessage, openMessageWindow, closeMessageWindow,
  } = useBrainChat();
  const {
    busy, workPending, submittedRun, ready, ask, cards, readOnly, childFocus,
    focusNonce, sendNonce, hasMoreHistory, planDecision, membership, conversationReadState, unreadBoundaryId, typing, turnWriter, programStatus,
  } = useBrainChatStatus();
  const viewer = useMe().data?.user;
  // The per-token read, and the ONLY one in this file: the transcript is what a token changes.
  const { turns: liveTurns, messageWindow, thinking, narration, conversationAppendCount: liveAppendCount } = useBrainChatTranscript();
  const turns = useMemo(() => messageWindow ? turnsFromHistory(messageWindow.items) : liveTurns, [messageWindow, liveTurns]);
  const conversationAppendCount = messageWindow ? undefined : liveAppendCount;
  const voiceCallProjection = useMemo(() => projectVoiceCallTurns(turns), [turns]);

  /** What a plugin chat dock view is told about a prompt waiting on the user (plugin UI API 15).
   *
   *  A view that expands into its own surface covers this surface — the question card included — so
   *  a reader watching, say, a live browser never learns that the agent stopped to ask them something.
   *  The contract is deliberately contentless: the app's own translated line, plus the way back to the
   *  card that owns answering. Nothing about the question crosses into a bundle, and nothing a bundle
   *  draws can drift from what the user is actually being asked.
   *
   *  `reveal` belongs to THIS surface, not to a lookup: a phone with the dock open on /chat mounts two,
   *  and each one hands its own dock views its own card. */
  const pendingInput = useMemo<PluginChatPendingInput | null>(() => (ask ? {
    label: t.brainChat.askWaiting,
    reveal: () => {
      const card = askRef.current;
      if (!card) return;
      card.scrollIntoView({ block: 'center', behavior: 'smooth' });
      card.focus({ preventScroll: true });
    },
  } : null), [ask, t.brainChat.askWaiting]);

  // Whether the statusline row (model / context / tokens / cost) is shown is a per-device display choice —
  // it belongs to the screen you are on, not the user record. Collapsing it in-chat (a small chevron) is
  // the quick alternative to the statusline plugin's settings toggles.
  const [statuslinePref, setStatuslinePref] = usePersistentState<'shown' | 'hidden'>('elowen.chat.statusline', 'shown', STATUSLINE_VALUES);
  const statuslineShown = statuslinePref === 'shown';
  const toggleStatusline = useCallback(
    () => setStatuslinePref(statuslineShown ? 'hidden' : 'shown'),
    [statuslineShown, setStatuslinePref],
  );
  // Running agents are reported in exactly ONE place. The docked rail lists them (and drills into them), so
  // while it is open the in-transcript chip is redundant — the same work was being announced twice.
  // `telemetryShown` is undefined wherever there is no docked rail (the compact dock, and a phone where the
  // rail is a drawer), so only an actually-visible rail takes ownership; hidden or absent hands the
  // reporting back to the transcript rather than dropping it.
  const railOwnsLiveWork = telemetryShown === true;
  // `undefined` until the viewport has actually been measured. Every branch below therefore tests `=== true`
  // or `=== false` and renders NOTHING in between: the boolean-returning hook reports `false` first, which
  // on a phone painted one frame of the desktop controls (inline picker, mode pill, reasoning button) before
  // swapping them for the ⋯ menu. A bar that is briefly missing a control is quieter than one that visibly
  // rearranges itself. Same approach as ChatView, which reads this hook for its own layout.
  const mobile = useMobileViewport();
  // Out-of-band controls wait for the viewport measurement too: rendering them while the viewport is still
  // unknown paints them once on a phone before the layout resolves.
  const ambientExtrasReady = mobile !== undefined;
  // The same settled claims and row-addressability predicate feed TelemetryPanel. A row-only rail
  // cannot replace a card's body. Settled claims keep their fallback in the transcript, even if pinned;
  // loading, absent and refused registrations retain generic placement without guessing an owner.
  const { footerCards, ambientCards } = useMemo(() => {
    const footerCards: BrainCard[] = [];
    const ambientCards: BrainCard[] = [];
    for (const card of cards) {
      if (isBackgroundProcessCardId(card.id)) continue;
      const items = card.items ?? [];
      if (items.length > 0 && items.every((item) => item.status === 'completed')) continue;
      const claimed = takeovers.ready && takeovers.byCard.has(card.id);
      if (railOwnsLiveWork && claimed && !card.body && cardItemsAddressable(card)) continue;
      if (card.pinned && !claimed) {
        footerCards.push(card);
      } else {
        ambientCards.push(card);
      }
    }
    return { footerCards, ambientCards };
  }, [cards, railOwnsLiveWork, takeovers]);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const transcriptContentRef = useRef<HTMLDivElement>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const dockPusherRef = useRef<HTMLDivElement>(null);
  const dockFloatRef = useRef<HTMLDivElement>(null);
  const [hasFloatingDock, setHasFloatingDock] = useState(false);
  const publishFloatingDock = useEffectEvent((next: boolean) => {
    if (hasFloatingDock !== next) setHasFloatingDock(next);
  });
  const surfaceRootRef = useRef<HTMLDivElement>(null);
  const [searchSession, setSearchSession] = useState<string | null>(null);
  const searchOpen = activeSessionId !== null && searchSession === activeSessionId;
  const searchFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => { setSearchSession(null); searchFocusRef.current = null; }, [activeSessionId]);
  const openSearch = useCallback(() => {
    if (!activeSessionId) return;
    if (document.activeElement instanceof HTMLElement) searchFocusRef.current = document.activeElement;
    setSearchSession(activeSessionId);
  }, [activeSessionId]);
  const closeSearch = useCallback(() => {
    setSearchSession(null);
    searchFocusRef.current?.focus({ preventScroll: true });
    searchFocusRef.current = null;
  }, []);
  const keyboardStatus = useMemo(() => <ChatKeyboard surfaceRef={surfaceRootRef} onTelemetry={onOpenTelemetry} onSearch={openSearch} />, [onOpenTelemetry, openSearch]);
  const composerDockRef = useRef<HTMLDivElement>(null);
  const askRef = useRef<HTMLDivElement>(null);
  const [showUnreadJump, setShowUnreadJump] = useState(false);
  useEffect(() => {
    const root = surfaceRootRef.current!;
    const scroll = scrollRef.current!;
    const end = transcriptEndRef.current!;
    const latestVisible = () => {
      if (searchOpen || messageWindow || root.closest('[inert]') || getComputedStyle(root).visibility !== 'visible') return false;
      const viewport = scroll.getBoundingClientRect();
      const tail = end.getBoundingClientRect();
      return viewport.width > 0 && viewport.height > 0 && tail.top >= viewport.top && tail.bottom <= viewport.bottom + 1;
    };
    const unregister = registerSurface(latestVisible);
    let previous = false;
    const update = () => {
      const visible = latestVisible();
      if (visible !== previous) { previous = visible; notifyActivityVisibility(); }
      const target = unreadBoundaryId ? root.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(unreadBoundaryId)}"]`) : null;
      const viewport = scroll.getBoundingClientRect();
      const box = target ? messageBox(target).getBoundingClientRect() : undefined;
      const boundaryVisible = box !== undefined && box.height > 0 && box.bottom > viewport.top && box.top < viewport.bottom;
      setShowUnreadJump(Boolean(unreadBoundaryId) && !visible && !boundaryVisible);
    };
    scroll.addEventListener('scroll', update);
    const resize = new ResizeObserver(update);
    resize.observe(scroll); resize.observe(end);
    resize.observe(transcriptContentRef.current!);
    const overlays = new MutationObserver(update);
    overlays.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['inert', 'hidden'] });
    update();
    return () => { scroll.removeEventListener('scroll', update); resize.disconnect(); overlays.disconnect(); unregister(); };
  }, [registerSurface, notifyActivityVisibility, searchOpen, messageWindow, unreadBoundaryId]);
  // Older history: ONE page request at a time, answered synchronously by `olderRef` for scroll events that
  // land before React renders; `olderState` only draws the spinner and the retry control. A failed page
  // halts paging until the reader asks again with the retry control: nothing retries on its own.
  const [olderState, setOlderState] = useState<'idle' | 'loading' | 'failed'>('idle');
  const olderRef = useRef({ inFlight: false, halted: false });
  const sentinelRef = useRef<HTMLDivElement>(null);
  // A touch gesture and its momentum must end before the transcript's position may be written; see
  // `scrollSettle.ts`. One gate per scroller, created with the scroll listeners.
  const settleRef = useRef<ScrollSettleGate | null>(null);
  // THE scroll state. `followNewestRef` owns whether layout changes keep the newest turn visible. It is
  // read from the position alone: a scroll that was not the layout owner's own write releases it when it
  // leaves the bottom zone and resumes it when it comes back down into it.
  const followNewestRef = useRef(true);
  // The offset the layout owner last wrote, so the `scroll` event that write causes is not mistaken for
  // the reader moving. -1 is "nothing pending", which no real offset equals. The next scroll event consumes
  // it whether or not it matched, so a reader who later lands on that same offset by hand still counts.
  const ownWriteAtRef = useRef(-1);
  // The offset and range the surface last took note of, so a move is seen once: by the `scroll` event or
  // by a settle that runs before that event is dispatched, whichever comes first.
  const knownRef = useRef({ top: 0, height: 0, client: 0 });
  // The layout owner's `settle`, bound while the owner is mounted, for the surface's own follow requests.
  const settleLayoutRef = useRef<() => void>(() => {});
  const previousSessionRef = useRef<string | null>(activeSessionId);
  // A run can grow at either end. Reuse its React key through a stored step already on screen, so
  // prepending its opener cannot discard the reader's disclosure choice or remount its content.
  const runKeysRef = useRef(new Map<string, string>());
  // Anchor the visible content inside a run, which can grow above the reader without moving its shell.
  // The source segment identity survives prepend/remount; its descendant path holds the paragraph
  // through late media inside that same segment. Coordinates include scrollTop, so momentum cancels out.
  const readingAnchorRef = useRef<{ tk: string; segment: string | null; path: number[]; top: number } | null>(null);

  // The transcript owns every conversation scroll on both surfaces. The shell page never participates in
  // follow-newest, focus recovery or lazy history loading, so mobile browser focus handling cannot combine
  // a page scroll with a second chat scroll.
  const getScroller = useCallback((): HTMLElement | null => scrollRef.current, []);

  // Remember which block the reader is looking at. The blocks are direct children of the content box in
  // document order, so the first one whose bottom is below the scroll offset is found by bisection.
  const recordReadingAnchor = useCallback((): void => {
    const s = getScroller();
    const content = transcriptContentRef.current;
    if (!s || !content) return;
    const blocks = content.querySelectorAll<HTMLElement>(':scope > [data-tk]');
    let low = 0;
    let high = blocks.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      const block = blocks[mid]!;
      if (block.offsetTop - s.offsetTop + block.offsetHeight > s.scrollTop) high = mid;
      else low = mid + 1;
    }
    const block = blocks[low];
    if (!block) { readingAnchorRef.current = null; return; }
    const visibleTop = s.getBoundingClientRect().top;
    const visible = (node: Element) => {
      const rect = node.getBoundingClientRect();
      return rect.height > 0 && rect.bottom > visibleTop && !node.closest('[inert]');
    };
    const segment = [...block.querySelectorAll<HTMLElement>('[data-chat-anchor]')]
      .find(node => node.firstElementChild !== null && visible(node.firstElementChild));
    const root = segment ?? block;
    const readable = [...root.querySelectorAll<HTMLElement>('p, li, pre, h1, h2, h3, h4, h5, h6, figure')].filter(visible);
    // A list item's top does not move when media inside it grows above the paragraph being read.
    const target = readable.find(node => !readable.some(child => child !== node && node.contains(child)))
      ?? segment?.firstElementChild ?? block;
    const path: number[] = [];
    for (let node = target; node !== root; node = node.parentElement!) {
      path.unshift(Array.prototype.indexOf.call(node.parentElement!.children, node));
    }
    readingAnchorRef.current = {
      tk: block.dataset.tk!, segment: segment?.dataset.chatAnchor ?? null, path,
      top: (target.getBoundingClientRect().top - visibleTop) / uiZoom() + s.scrollTop,
    };
  }, [getScroller]);

  // Hold the visible paragraph through a layout change, including an older page extending this run.
  // Resolve it by source identity so a reconciled DOM never replaces the intended reading point.
  const holdReadingAnchor = useCallback((): void => {
    const s = getScroller();
    const anchor = readingAnchorRef.current;
    if (s && anchor) {
      const block = transcriptContentRef.current?.querySelector<HTMLElement>(`:scope > [data-tk="${CSS.escape(anchor.tk)}"]`);
      let target: Element | null | undefined = anchor.segment === null ? block
        : block?.querySelector(`[data-chat-anchor="${CSS.escape(anchor.segment)}"]`);
      for (const index of anchor.path) target = target?.children[index];
      if (target) {
        const top = (target.getBoundingClientRect().top - s.getBoundingClientRect().top) / uiZoom() + s.scrollTop;
        const moved = top - anchor.top;
        if (Math.abs(moved) > 0.5) s.scrollTop += moved;
      }
    }
    recordReadingAnchor();
  }, [getScroller, recordReadingAnchor]);

  // Re-entering a conversation is an explicit request to see its newest turn: the pin goes back on.
  const followNewestAgain = useCallback((): void => {
    followNewestRef.current = true;
    readingAnchorRef.current = null;
    settleLayoutRef.current();
  }, []);

  // ── OLDER HISTORY ──────────────────────────────────────────────────────────────────────────────────
  // A failed page, whoever asked for it (the paging rule below or a jump), halts paging behind the one
  // retry control. The missing slice is a state of the transcript's region, so it is stated there with its
  // retry and is not also a toast.
  const haltOlder = useCallback((): void => {
    olderRef.current.halted = true;
    setOlderState('failed');
  }, []);
  // One rule: while older history remains and the oldest loaded turn is within the prefetch band above the
  // scrollport, request the next page, one at a time. The page is committed once the scroller is settled
  // (`loadOlder` waits for the gate), the commit holds the reading anchor before paint, and the rule is
  // asked again, so pages that fold into a collapsed run without adding height keep coming until the band
  // holds content, while a stationary reader with a filled band requests nothing more.
  const requestOlder = useEffectEvent((): void => {
    const older = olderRef.current;
    if (older.inFlight || older.halted || !hasMoreHistory || messageWindow || searchOpen) return;
    older.inFlight = true;
    setOlderState('loading');
    loadOlder(() => settleRef.current?.whenSettled() ?? Promise.resolve()).then(
      () => {
        older.inFlight = false;
        setOlderState('idle');
        // The rule is asked again by a settle, after the one that held the reading anchor over the
        // committed page; asked against the offset before it, it fetched pages the reader had not approached.
        settleLayoutRef.current();
      },
      () => {
        older.inFlight = false;
        haltOlder();
      },
    );
  });
  const fillOlderBand = useEffectEvent((): void => {
    const s = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!s || !sentinel) return;
    // The rule answers for the READER's position, so while the surface still follows the newest turn only
    // the bottom of the transcript may ask: until the follow write lands, the offset is wherever the browser
    // painted the page (0 on entry), where the oldest turn is "in the band" by definition. An opening settle
    // that only a composer resize triggered skips that write by design, and asking there fetched an older
    // page for a reader who had not entered the history at all.
    if (followNewestRef.current && s.scrollHeight - s.scrollTop - s.clientHeight > 1) return;
    const view = s.getBoundingClientRect();
    if (sentinel.getBoundingClientRect().bottom > view.top - view.height * OLDER_BAND_SCREENS) requestOlder();
  });
  // The retry control lifts the halt; the paging effect below re-evaluates the rule on the state change.
  const retryOlder = useCallback((): void => {
    olderRef.current.halted = false;
    setOlderState('idle');
  }, []);

  // ── SHARED-CONVERSATION CHROME ─────────────────────────────────────────────────────────────────────
  // A member reacts in the conversation bound on screen; administrator oversight and child views only read.
  // One control object per conversation and viewer keeps every memoized message's identity.
  const mayReact = !messageWindow && !readOnly && !childFocus && membership?.capabilities.continue === true;
  const viewerId = viewer?.id;
  const viewerName = viewer?.name;
  const ownerId = membership?.ownerId;
  const reacting = useMemo<ReactionControl | undefined>(() => (
    mayReact && activeSessionId && viewerId !== undefined && viewerName !== undefined && ownerId !== undefined
      ? { viewer: { id: viewerId, name: viewerName }, ownerId, toggle: (messageId, emoji, add) => reactToMessage(activeSessionId, messageId, emoji, add) }
      : undefined
  ), [mayReact, activeSessionId, viewerId, viewerName, ownerId, reactToMessage]);

  // Reply targets the jump could not reach, for the conversation they belong to.
  const [goneQuotes, setGoneQuotes] = useState<{ session: string | null; ids: ReadonlySet<string> }>({ session: null, ids: NO_IDS });
  const gone = goneQuotes.session === activeSessionId ? goneQuotes.ids : NO_IDS;
  // A target the bounded jump did not reach is no longer gone once a later page loads it after all.
  if (gone.size && turns.some((turn) => turn.id !== undefined && gone.has(turn.id))) {
    const loaded = new Set(turns.flatMap((turn) => (turn.id === undefined ? [] : [turn.id])));
    setGoneQuotes({ session: activeSessionId, ids: new Set([...gone].filter((id) => !loaded.has(id))) });
  }
  const quotes = useReplyQuotes(turns, activeSessionId, membership?.owner ?? null, gone);
  // The members a stored message's mention chips take their current names and avatars from.
  const people = useMemo(() => (membership
    ? new Map([membership.owner, ...membership.participants].map((profile) => [profile.id, profile]))
    : undefined), [membership]);
  const jumpLatestRef = useRef({ session: activeSessionId, turns });
  useLayoutEffect(() => { jumpLatestRef.current = { session: activeSessionId, turns }; });
  // A jump target folded inside a settled run: the run that holds it is asked to open (its own disclosure
  // state stays its own), and the jump continues once that commit is on screen.
  const [reveal, setReveal] = useState<{ session: string | null; id: string; nonce: number } | null>(null);
  const revealWaitersRef = useRef(new Map<number, () => void>());
  useLayoutEffect(() => {
    if (!reveal) return;
    revealWaitersRef.current.get(reveal.nonce)?.();
    revealWaitersRef.current.delete(reveal.nonce);
  }, [reveal]);
  const revealNonceRef = useRef(0);
  const revealMessage = useCallback((id: string): Promise<void> => new Promise((resolve) => {
    const nonce = ++revealNonceRef.current;
    revealWaitersRef.current.set(nonce, resolve);
    setReveal({ session: jumpLatestRef.current.session, id, nonce });
  }), []);
  const jumpGeneration = useRef(0);
  const windowReadPending = useRef(false);
  const [windowJump, setWindowJump] = useState<{ session: string; id: string; generation: number; keepFocus: boolean } | null>(null);
  useLayoutEffect(() => () => { jumpGeneration.current += 1; }, [activeSessionId]);
  const focusMessage = useCallback(async (id: string, session: string | null, generation: number, keepFocus: boolean): Promise<boolean> => {
    const find = () => transcriptContentRef.current?.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(id)}"]`) ?? null;
    let target = find();
    if ((!target || target.closest('[inert]')) && jumpLatestRef.current.turns.some((turn) => turn.id === id)) {
      await revealMessage(id);
      if (jumpLatestRef.current.session !== session || jumpGeneration.current !== generation) return false;
      target = find();
    }
    if (!target) return false;
    const box = messageBox(target);
    followNewestRef.current = false;
    readingAnchorRef.current = null;
    box.scrollIntoView({ block: 'center' });
    if (!box.hasAttribute('tabindex')) box.tabIndex = -1;
    if (!keepFocus) box.focus({ preventScroll: true });
    box.classList.add(...JUMP_RING);
    window.setTimeout(() => box.classList.remove(...JUMP_RING), JUMP_RING_MS);
    return true;
  }, [revealMessage]);
  /** All callers share this scroll owner. A missing loaded target is read in one bounded around window,
   * never by merging unrelated pages into the live tail. */
  const jumpToMessage = useCallback(async (id: string, keepFocus = false): Promise<'found' | 'missing' | 'failed' | 'abandoned'> => {
    const session = jumpLatestRef.current.session;
    const generation = ++jumpGeneration.current;
    if (!session) return 'abandoned';
    const cancelledRead = windowReadPending.current;
    if (cancelledRead) { closeMessageWindow(); windowReadPending.current = false; }
    if (!cancelledRead && jumpLatestRef.current.turns.some((turn) => turn.id === id)) {
      return await focusMessage(id, session, generation, keepFocus) ? 'found' : 'abandoned';
    }
    windowReadPending.current = true;
    try {
      await openMessageWindow(id);
      if (jumpLatestRef.current.session !== session || jumpGeneration.current !== generation) return 'abandoned';
      setWindowJump({ session, id, generation, keepFocus });
      return 'found';
    } catch (error) {
      if (jumpLatestRef.current.session !== session || jumpGeneration.current !== generation) return 'abandoned';
      return error instanceof ElowenApiError && error.status === 404 ? 'missing' : 'failed';
    } finally {
      if (jumpGeneration.current === generation) windowReadPending.current = false;
    }
  }, [focusMessage, openMessageWindow, closeMessageWindow]);
  const jumpFromFooter = useCallback(async (id: string): Promise<void> => {
    const outcome = await jumpToMessage(id);
    if (outcome === 'missing') toast(t.chat.messageUnavailable, 'error');
    else if (outcome === 'failed') toast(t.brainChat.searchOpenError, 'error');
  }, [jumpToMessage, toast, t.chat.messageUnavailable, t.brainChat.searchOpenError]);
  useEffect(() => {
    if (!windowJump || !messageWindow || windowJump.session !== activeSessionId || windowJump.generation !== jumpGeneration.current) return;
    setWindowJump(null);
    if (!turns.some((turn) => turn.id === windowJump.id)) {
      toast(t.chat.messageUnavailable, 'error');
      return;
    }
    void focusMessage(windowJump.id, windowJump.session, windowJump.generation, windowJump.keepFocus);
  }, [windowJump, messageWindow, activeSessionId, turns, focusMessage, toast, t.chat.messageUnavailable]);
  const returnToLatest = useCallback(() => {
    jumpGeneration.current += 1;
    windowReadPending.current = false;
    setWindowJump(null);
    closeMessageWindow();
    closeSearch();
    followNewestAgain();
  }, [closeMessageWindow, closeSearch, followNewestAgain]);
  const quoteKey = JSON.stringify([...quotes].map(([id, quote]) => [id, quote.status === 'ready'
    ? [quote.author.kind === 'user' ? [quote.author.profile.id, quote.author.profile.name, quote.author.profile.avatar] : 'agent', quoteSnippet(quote.text)]
    : quote.status]));
  // Rebuilt only when what a quote shows changes, never per streamed token, so settled runs stay memoized.
  const quoteFor = useMemo<ReplyQuoteFor | undefined>(() => (quotes.size === 0 ? undefined : (step) => {
    const id = step.replyTo?.id;
    const quote = id === undefined ? undefined : quotes.get(id);
    if (id === undefined || !quote) return null;
    return { quote, onJump: () => {
      const session = activeSessionId;
      void jumpToMessage(id).then((outcome) => {
        if (outcome === 'missing') setGoneQuotes((current) => ({ session, ids: new Set([...(current.session === session ? current.ids : []), id]) }));
        else if (outcome === 'failed') toast(t.brainChat.searchOpenError, 'error');
      });
    } };
  }), [quoteKey, jumpToMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Index of the first live (id-less) turn — the boundary between stored history and the live streaming tail
  // used to key the tail stably across a lazy-load prepend (see the transcript map below).
  const firstLiveTurn = turns.findIndex((turn) => !turn.id);


  // First mount of ANY chat surface (dock opened in chat mode) lazily boots the controller. Idempotent —
  // a second mount (or the BRAIN_* window events) never re-runs brainStart, so a one-shot mount call is
  // enough (and avoids re-firing on the controller's per-render identity churn). A linked conversation IS
  // that attachment: opened from the page instead, it ran after this child effect, so the default
  // conversation attached first and the link was lost.
  useEffect(() => {
    if (openSessionId) openConversationLink(openSessionId);
    else ensureAttached();
  }, [openSessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  // The link's message is looked for once its conversation is on screen; a message that is gone says so and
  // leaves the conversation at its end.
  const linkedMessageRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openSessionId || !openMessageId || !ready || activeSessionId !== openSessionId) return;
    const link = `${openSessionId}:${openMessageId}`;
    if (linkedMessageRef.current === link) return;
    linkedMessageRef.current = link;
    void jumpToMessage(openMessageId).then((outcome) => {
      if (outcome === 'failed') toast(t.brainChat.searchOpenError, 'error');
      else if (outcome === 'missing') {
        toast(t.chat.messageUnavailable, 'error');
        followNewestAgain();
      }
    });
  }, [openSessionId, openMessageId, ready, activeSessionId, jumpToMessage, toast, t.brainChat.searchOpenError, t.chat.messageUnavailable, followNewestAgain]);

  // ── THE LAYOUT OWNER ───────────────────────────────────────────────────────────────────────────────
  // Every write of the transcript's scroll offset and every piece of dock float geometry comes from the one
  // `settle` below. In order: it settles the dock float (full page, floating Browser/Project tiles), then
  // applies the follow decision to that final layout, then asks the paging rule. The order is the point.
  // Solving the float toggles it off and on while probing, which shrinks the scroll range for a moment and
  // lets the browser clamp the offset; a decision taken before the solve was undone by it, left the tail
  // one line under the composer and, past the bottom zone, read as the reader leaving. Nothing else may
  // write the offset: a second writer would correct this one, and the two would disagree about where the
  // bottom is. Nothing else may ask the paging rule either: asked before a settle, it measures the
  // sentinel against a stale offset and fetches pages the reader did not approach.
  //
  // Triggers: the transcript's content mutating (a streamed token, a prepended page, a details toggle),
  // either box resizing (late media, fonts, a growing composer, the keyboard), a tile's box, the viewport
  // inset, a window resize, media load/error in the transcript and font loading; opening a surface or
  // another conversation asks for the newest turn through `followNewestAgain`. Each settles at once, in the
  // mutation's own microtask or event, so no later code, no other animation-frame callback and no paint
  // sees the transcript at a stale offset. The one deferral is a box resize while tiles float: a
  // ResizeObserver callback must not change the sizes it observes, and the float solve does, so that
  // settle waits for the next animation frame. Mutations and media inside a floating tile (action text,
  // meters, a still, a connection row) never settle: the tile is out of flow and only its box, which is
  // observed, can move the transcript.
  // Read the reader's intent from the position. The `scroll` event is asynchronous, so a settle that runs
  // between a hand scroll and its event would otherwise follow a stale state; both ask here, and the
  // second one finds nothing new.
  const noteReaderPosition = useEffectEvent((): void => {
    const s = scrollRef.current!;
    const top = s.scrollTop;
    const height = s.scrollHeight;
    const client = s.clientHeight;
    const known = knownRef.current;
    knownRef.current = { top, height, client };
    if (top === known.top) return;
    recordReadingAnchor();
    // The layout owner's own write, read back — never the reader. Consumed here, so it cannot shadow a
    // later reader scroll that happens to stop on the same offset.
    const own = top === ownWriteAtRef.current;
    ownWriteAtRef.current = -1;
    if (own) return;
    const gap = height - top - client;
    // A move that arrives with a change of the scroll range is the browser clamping the offset, not the
    // reader: the composer shrank while idle, or a forced layout mid-animation (a timeline fold measuring
    // its closed height) shortened the content for an instant. Landing on the bottom follows; any other
    // clamp changes nothing.
    if (height !== known.height || client !== known.client) {
      if (gap < 1) followNewestRef.current = true;
      return;
    }
    // The gate exists once the scroll listeners are installed, after the owner's first settle.
    settleRef.current?.scrolled();
    // Following resumes only on the way DOWN into the bottom zone. A finger leaving the bottom crosses
    // that zone in its first frames, and re-enabling there let the next streamed token pin the reader
    // back down.
    followNewestRef.current = gap < BOTTOM_GAP && (top >= known.top || gap < 1);
  });
  const decide = useEffectEvent((composerOnly: boolean): void => {
    const s = scrollRef.current!;
    // A reader in the history keeps their block in place through late media, fonts, reflow and prepends.
    if (!followNewestRef.current) holdReadingAnchor();
    // An idle draft preserves the reading position. During a live turn, following newest must also
    // consume editor growth, otherwise the tail is clipped until the next stream delta arrives.
    else if (!composerOnly || busy) s.scrollTo({ top: s.scrollHeight });
    // Read the offset BACK rather than assuming `scrollHeight`: the browser clamps the write, and the
    // clamped value is what the resulting `scroll` event will report.
    ownWriteAtRef.current = s.scrollTop;
    // A taller scrollport widens the prefetch band; a shorter transcript brings the sentinel closer.
    fillOlderBand();
  });
  useLayoutEffect(() => {
    const root = surfaceRootRef.current!;
    const transcript = scrollRef.current!;
    const contentRoot = transcriptContentRef.current!;
    const end = transcriptEndRef.current!;
    const dock = composerDockRef.current!;

    // ── The dock float. React owns the two sibling floats; the solve moves them before the first block
    // that can meet the band, so probes invalidate only the suffix. Their local variables never invalidate
    // inherited styles across the entire history. The end sentinel measures FLOW, not the BFC's
    // float-inclusive height.
    let previous: number | null = null;
    let previousEnd = 0;
    let trimmedTail: HTMLElement | null = null;
    let contentDirty = true;
    let solution: { end: number; width: number; height: number; tileWidth: number; clientHeight: number; dockTop: number } | null = null;
    const placed = new Set<HTMLElement>();
    const write = (element: HTMLElement, name: string, value: string | null): void => {
      if (value === null) {
        if (element.style.getPropertyValue(name)) element.style.removeProperty(name);
      } else if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
    };
    const publish = (name: string, value: string | null): void => write(root, name, value);
    const trimTail = (tail: HTMLElement | null): void => {
      if (trimmedTail === tail) return;
      trimmedTail?.removeAttribute('data-chat-dock-tail');
      trimmedTail = tail;
      trimmedTail?.setAttribute('data-chat-dock-tail', '');
    };
    const clearFloat = (): void => {
      trimTail(null);
      previous = null;
      solution = null;
      if (dockPusherRef.current) write(dockPusherRef.current, '--chat-dock-float-top', null);
      if (dockFloatRef.current) write(dockFloatRef.current, '--chat-dock-float-height', null);
    };
    const solveDock = (): void => {
      const tiles = floatingDockTiles(root);
      const stackGap = DOCK_STACK_GAP_REM * remPx();
      let stacked = 0;
      let left = Infinity;
      let right = -Infinity;
      const present = new Set(tiles);
      for (const tile of tiles) {
        const rect = tile.getBoundingClientRect();
        write(tile, '--chat-dock-offset', `${Math.round(stacked)}px`);
        if (!placed.has(tile)) { placed.add(tile); resize.observe(tile); }
        stacked += rect.height + stackGap;
        left = Math.min(left, rect.left);
        right = Math.max(right, rect.right);
      }
      for (const tile of placed) {
        if (present.has(tile)) continue;
        placed.delete(tile);
        resize.unobserve(tile);
        write(tile, '--chat-dock-offset', null);
      }
      const height = tiles.length > 0 ? Math.ceil(stacked - stackGap) : 0;
      publish('--chat-composer-height', height > 0 ? `${Math.ceil(dock.getBoundingClientRect().height)}px` : null);
      publish('--chat-dock-width', height > 0 ? `${Math.ceil(right - left)}px` : null);
      publishFloatingDock(height > 0);
      if (height === 0) { clearFloat(); return; }

      // React mounts the boxes on discovery. Its child-list notification settles the next frame.
      const pusher = dockPusherRef.current;
      const exclusion = dockFloatRef.current;
      if (!pusher || !exclusion) return;
      const contentBox = contentRoot.getBoundingClientRect();
      const liveEnd = end.getBoundingClientRect().top - contentBox.top;
      const dockTop = dock.getBoundingClientRect().top;
      const tileWidth = Math.ceil(right - left);
      if (!contentDirty && solution && solution.end === liveEnd && solution.width === contentBox.width &&
        solution.height === height && solution.tileWidth === tileWidth &&
        solution.clientHeight === transcript.clientHeight && solution.dockTop === dockTop) {
        // A resize caused by our own solved flow needs only this read. A text/details/media/font
        // mutation ALWAYS invalidates the proof, even if its full-width end happens to stay equal.
        return;
      }
      contentDirty = false;
      write(pusher, 'height', '0px');
      write(exclusion, 'height', '0px');
      trimTail(null);
      const endWithoutFloat = end.getBoundingClientRect().top - contentRoot.getBoundingClientRect().top;
      const padding = getComputedStyle(transcript);
      const padTop = parseFloat(padding.paddingTop) || 0;
      const padBottom = parseFloat(padding.paddingBottom) || 0;
      const offScroll = Math.max(0, endWithoutFloat + padTop + padBottom - transcript.clientHeight);
      const offEndAtBottom = transcript.getBoundingClientRect().top + padTop + endWithoutFloat - offScroll;
      const gap = DOCK_TILE_GAP_REM * remPx();
      // Decide from the layout WITHOUT floats. Applying one cannot flip its own eligibility.
      const reachesDock = offEndAtBottom > dock.getBoundingClientRect().top - gap - height;
      const floatHeight = height + gap;
      // Eligibility uses the original off-layout rhythm. Search the active tail boundary in
      // the SAME frame, after removing only the separators that have no following content.
      // The last turn's last segment carries the trailing space to trim, whatever wraps it: a message block
      // holding its own row, or the closing message of a run, whose row sits in the run's head line above.
      const tailTurn = reachesDock ? lastTurnBeforeEnd(end) : null;
      const tailSegments = tailTurn ? [...tailTurn.querySelectorAll<HTMLElement>('.chat-turn__body .chat-turn__segment')] : [];
      if (reachesDock) trimTail(tailSegments.at(-1) ?? null);
      const searchEnd = reachesDock
        ? end.getBoundingClientRect().top - contentRoot.getBoundingClientRect().top
        : endWithoutFloat;
      const contentTop = contentRoot.getBoundingClientRect().top;
      const low = Math.max(0, Math.floor(searchEnd - floatHeight));
      // A floating tile of the chat dock is positioned out of flow, so it is never the block the floats
      // go before.
      const blocks = [...contentRoot.children].filter((child): child is HTMLElement =>
        child instanceof HTMLElement && child !== pusher && child !== exclusion && child !== end && !child.matches('[data-chat-dock]'));
      // Work backwards through only the tail. Earlier turns remain in the SAME BFC, before the
      // floats, and retain their cached layout fragments. No positioned wrapper or nested BFC.
      const anchor = blocks.findLast(child =>
        getComputedStyle(child).display !== 'none' && child.getBoundingClientRect().top - contentTop <= low)!;
      if (exclusion.nextElementSibling !== anchor || pusher.nextElementSibling !== exclusion) {
        contentRoot.insertBefore(pusher, anchor);
        contentRoot.insertBefore(exclusion, anchor);
      }
      const origin = exclusion.getBoundingClientRect().top - contentRoot.getBoundingClientRect().top;
      const top = chatDockFloatTop({
        endWithoutFloat: searchEnd - origin, height: floatHeight, reachesDock,
        // A streamed delta grows the tail below the float, so the first fitting pixel moved down by about
        // as much as the unwrapped end did: start the search there, not at the stale previous top.
        previous: previous === null ? null : Math.round(previous + searchEnd - previousEnd - origin),
        endAt: y => {
          write(pusher, 'height', `${y}px`);
          write(exclusion, 'height', `${floatHeight}px`);
          return end.getBoundingClientRect().top - contentRoot.getBoundingClientRect().top - origin;
        },
      });
      if (top === null) trimTail(null);
      previous = top === null ? null : top + origin;
      previousEnd = searchEnd;
      write(pusher, '--chat-dock-float-top', top === null ? null : `${top}px`);
      write(exclusion, '--chat-dock-float-height', top === null ? null : `${floatHeight}px`);
      write(pusher, 'height', null);
      write(exclusion, 'height', null);
      solution = { end: end.getBoundingClientRect().top - contentRoot.getBoundingClientRect().top,
        width: contentBox.width, height, tileWidth, clientHeight: transcript.clientHeight, dockTop };
    };

    // ── What the previous settle saw of the composer and the viewport, for the idle-draft rule.
    let composerHeight = dock.getBoundingClientRect().height;
    let viewportWidth = window.visualViewport?.width ?? window.innerWidth;
    let viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    let viewportInset = root.style.getPropertyValue('--chat-visual-bottom-offset');
    let keyboardOpen = root.dataset.chatKeyboardOpen;
    // The content box is a flow-root whose height the exclusion float extends: once the float ends at or
    // under the in-flow end, in-flow content that only shrinks (a work timeline folding, a disclosure
    // animating shut) leaves that height unchanged, so the box alone would never report it and the float
    // would hang below the text. Every block of the transcript is observed as well; a block shrinking is a
    // notification like any other.
    const observedBlocks = new Set<Element>();
    const observeBlocks = (): void => {
      for (const block of observedBlocks) if (!block.isConnected) { observedBlocks.delete(block); resize.unobserve(block); }
      for (const child of contentRoot.children) {
        if (child === dockPusherRef.current || child === dockFloatRef.current || observedBlocks.has(child)) continue;
        observedBlocks.add(child);
        resize.observe(child);
      }
    };
    let frame = 0;
    const settle = (): void => {
      cancelAnimationFrame(frame);
      frame = 0;
      observeBlocks();
      noteReaderPosition();
      const offset = transcript.scrollTop;
      if (variant === 'full') solveDock();
      // A probe may have clamped the offset while the float was off. Put it back before deciding; the
      // browser clamps it again to the FINAL range, which is the one that counts.
      if (transcript.scrollTop !== offset) transcript.scrollTop = offset;
      const nextComposerHeight = dock.getBoundingClientRect().height;
      const nextViewportWidth = window.visualViewport?.width ?? window.innerWidth;
      const nextViewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const nextInset = root.style.getPropertyValue('--chat-visual-bottom-offset');
      const nextKeyboardOpen = root.dataset.chatKeyboardOpen;
      // Opening a keyboard also removes footer safe-area padding. That is a viewport change,
      // not idle editor growth, even when both boxes resize in the same delivery.
      const viewportChanged = nextViewportWidth !== viewportWidth || nextViewportHeight !== viewportHeight
        || nextInset !== viewportInset || nextKeyboardOpen !== keyboardOpen;
      const composerResized = nextComposerHeight !== composerHeight;
      composerHeight = nextComposerHeight;
      viewportWidth = nextViewportWidth;
      viewportHeight = nextViewportHeight;
      viewportInset = nextInset;
      keyboardOpen = nextKeyboardOpen;
      decide(composerResized && !viewportChanged);
    };
    const request = (): void => { if (!frame) frame = requestAnimationFrame(settle); };
    const invalidate = (): void => { contentDirty = true; settle(); };
    settleLayoutRef.current = settle;

    const resize = new ResizeObserver(() => { if (variant === 'full' && placed.size > 0) request(); else settle(); });
    resize.observe(transcript);
    resize.observe(contentRoot);
    resize.observe(dock);
    const children = new MutationObserver(records => {
      const floats = [dockPusherRef.current, dockFloatRef.current];
      if (records.every(record => {
        // The solve's own float relocation, once there is a solution to keep.
        if (solution && record.type === 'childList' &&
          [...record.addedNodes, ...record.removedNodes].every(node => floats.includes(node as HTMLDivElement))) return true;
        // Only the tile root's own attributes (expansion) change what the solve must see.
        const element = record.target instanceof Element ? record.target : record.target.parentElement;
        const tile = element?.closest('[data-chat-dock]');
        return !!tile && (element !== tile || record.type !== 'attributes');
      })) return;
      invalidate();
    });
    children.observe(contentRoot, {
      childList: true, characterData: true, subtree: true, attributes: true,
      attributeFilter: ['data-expanded', 'open'],
    });
    // Observe only the external viewport inset, not our own published geometry. Local probes never
    // notify this observer, and an unchanged solution never rewrites a published variable.
    let observedInset = root.style.getPropertyValue('--chat-visual-bottom-offset');
    const style = new MutationObserver(() => {
      const next = root.style.getPropertyValue('--chat-visual-bottom-offset');
      if (next === observedInset) return;
      observedInset = next;
      invalidate();
    });
    style.observe(root, { attributes: true, attributeFilter: ['style'] });
    window.addEventListener('resize', invalidate);
    // A contained float can keep the BFC height unchanged when late media/fonts move its in-flow end. A
    // still refreshing inside a tile is not such media.
    const media = (event: Event): void => {
      if (event.target instanceof Element && event.target.closest('[data-chat-dock]')) return;
      invalidate();
    };
    contentRoot.addEventListener('load', media, true);
    contentRoot.addEventListener('error', media, true);
    document.fonts?.addEventListener('loadingdone', invalidate);
    // Opening a chat surface, the full route or the dock, always shows the newest turn. Each transcript
    // scroller is surface-owned, so it starts from a clean offset rather than inheriting another position.
    followNewestAgain();
    return () => {
      cancelAnimationFrame(frame);
      settleLayoutRef.current = () => {};
      resize.disconnect();
      children.disconnect();
      style.disconnect();
      window.removeEventListener('resize', invalidate);
      contentRoot.removeEventListener('load', media, true);
      contentRoot.removeEventListener('error', media, true);
      document.fonts?.removeEventListener('loadingdone', invalidate);
      clearFloat();
      for (const tile of placed) write(tile, '--chat-dock-offset', null);
      for (const name of ['--chat-dock-width', '--chat-composer-height']) publish(name, null);
    };
  }, [variant, followNewestAgain]);

  // Opening another conversation is an explicit request to see that conversation's newest message. Reset
  // every scroll/prepend guard before its snapshot lands; otherwise a chat opened while the previous one
  // was scrolled up inherited a released pin and rendered at the old page offset.
  useLayoutEffect(() => {
    if (!activeSessionId || activeSessionId === previousSessionRef.current) return;
    previousSessionRef.current = activeSessionId;
    followNewestAgain();
  }, [activeSessionId, followNewestAgain]);

  // Sending a message is the reader's own request to see the tail of the conversation, like entering it:
  // the pin goes back on even when they had scrolled up into the history. Only a send made from this window
  // counts; a turn that arrives from the agent or another device never moves a reader who is up there. As
  // with the focus nonce below, only an actual bump after mount counts.
  const lastSendRef = useRef(sendNonce);
  useLayoutEffect(() => {
    if (sendNonce === lastSendRef.current) return;
    lastSendRef.current = sendNonce;
    followNewestAgain();
  }, [sendNonce, followNewestAgain]);

  // Watch the transcript's live scroll position: take note of the reader's position and ask the paging
  // rule. The touch settle gate lives here too.
  useEffect(() => {
    const s = getScroller();
    if (!s) return;
    const settle = createScrollSettleGate();
    settleRef.current = settle;
    const onTouchStart = (): void => settle.touchStart();
    const onTouchEnd = (): void => settle.touchEnd();
    const onScroll = (): void => {
      noteReaderPosition();
      fillOlderBand();
    };
    s.addEventListener('scroll', onScroll, { passive: true });
    s.addEventListener('touchstart', onTouchStart, { passive: true });
    s.addEventListener('touchend', onTouchEnd, { passive: true });
    s.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      s.removeEventListener('scroll', onScroll);
      s.removeEventListener('touchstart', onTouchStart);
      s.removeEventListener('touchend', onTouchEnd);
      s.removeEventListener('touchcancel', onTouchEnd);
      settle.dispose();
      if (settleRef.current === settle) settleRef.current = null;
    };
  }, [getScroller]);

  // The controller asks the composer to focus (a compose-bridge request / a seeded draft) by bumping the
  // focus nonce — the surface owns the DOM ref, so it does the actual focus. Guard against a plain (re)mount
  // (Chat↔Terminál toggle, dock reopen) stealing focus: only an ACTUAL bump after mount focuses, never the
  // nonce value the surface happened to mount with.
  const lastFocusRef = useRef(focusNonce);
  useEffect(() => {
    if (focusNonce === lastFocusRef.current) return;
    lastFocusRef.current = focusNonce;
    requestAnimationFrame(() => composerRef.current?.focus());
  }, [focusNonce]);


  // Keyboard policies can resize the visual viewport and rendered shell independently. Measure their
  // actual overlap once as surface padding, without assuming that innerHeight equals the shell's edge.
  // The composer remains a normal flex child and the transcript is the only element that scrolls.
  // Safe-area padding is disabled while the keyboard is open because the visual viewport already ends above
  // the keyboard/home indicator.
  useLayoutEffect(() => {
    if (variant !== 'full') return;
    const root = surfaceRootRef.current;
    const composer = composerRef.current;
    if (!root || !composer) return;

    const viewport = window.visualViewport;
    const viewportHeight = () => window.visualViewport?.height ?? window.innerHeight;
    const viewportWidth = () => window.visualViewport?.width ?? window.innerWidth;
    let restingHeight = viewportHeight();
    let restingWidth = viewportWidth();
    let keyboardSessionOpen = false;
    let frame = 0;

    const setPx = (name: string, value: number) => {
      const next = `${Math.max(0, Math.round(value))}px`;
      if (root.style.getPropertyValue(name) !== next) root.style.setProperty(name, next);
    };
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const currentViewport = window.visualViewport;
        const currentHeight = viewportHeight();
        const currentWidth = viewportWidth();
        const focused = document.activeElement === composer;
        // Both edges are in layout-viewport coordinates. innerHeight is not the bottom of our dvh
        // shell on Safari; subtracting from it can pay for a keyboard the layout already consumed.
        const surfaceBottom = root.getBoundingClientRect().bottom;
        const visibleBottom = (currentViewport?.offsetTop ?? 0) + currentHeight;
        const overlap = Math.max(0, surfaceBottom - visibleBottom);
        const restingLandscape = restingWidth > restingHeight;
        const currentLandscape = currentWidth > currentHeight;
        const layoutRotated = keyboardSessionOpen
          && Math.abs(currentWidth - restingWidth) >= 1
          && currentLandscape !== restingLandscape;
        if (layoutRotated) {
          // A pending close after blur also survives rotation. Project the resting height from the old
          // layout's width (the axes exchanged), rather than comparing the new viewport to a baseline
          // from the old orientation, which can falsely close the keyboard in one direction.
          const previousRestingWidth = restingWidth;
          restingWidth = currentWidth;
          restingHeight = previousRestingWidth;
        }
        // Blur ends the composer's inset, not the keyboard's viewport lifecycle. The surface itself
        // may already be keyboard-sized, so fitting inside it is NOT proof of a resting viewport.
        // Keep the original baseline until the visible height returns, even across repeated blur/resize
        // events. Rotation projects that baseline first; it must never learn the reduced visual height.
        if (currentHeight >= restingHeight - 1) keyboardSessionOpen = false;
        if (!focused && !keyboardSessionOpen && currentHeight >= surfaceBottom - 1) {
          restingHeight = currentHeight;
          restingWidth = currentWidth;
        }
        const keyboardOpen = focused && (overlap > 1 || currentHeight < restingHeight - 1 || layoutRotated);
        keyboardSessionOpen ||= keyboardOpen;
        // Padding may only remove the part of THIS surface below the visible band. Its border box
        // stays fixed, so measuring it cannot feed the padding back into the next calculation.
        const visualBottomOffset = keyboardOpen && currentViewport ? overlap / uiZoom() : 0;

        root.dataset.chatKeyboardOpen = keyboardOpen ? 'true' : 'false';
        setPx('--chat-visual-bottom-offset', visualBottomOffset);
        // The transcript resize observer consumes the changed reading height and owns following it.
      });
    };
    // SwiftKey/WebKit need not deliver a final visual resize/scroll with the layout's return. Observe
    // the actual border box as well; observing the content box would respond to our own inset writes.
    const observer = new ResizeObserver(measure);
    observer.observe(root, { box: 'border-box' });
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('pageshow', measure);
    document.addEventListener('visibilitychange', measure);
    viewport?.addEventListener('resize', measure);
    viewport?.addEventListener('scroll', measure);
    composer.addEventListener('focus', measure);
    composer.addEventListener('blur', measure);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
      window.removeEventListener('pageshow', measure);
      document.removeEventListener('visibilitychange', measure);
      viewport?.removeEventListener('resize', measure);
      viewport?.removeEventListener('scroll', measure);
      composer.removeEventListener('focus', measure);
      composer.removeEventListener('blur', measure);
      root.style.removeProperty('--chat-visual-bottom-offset');
      delete root.dataset.chatKeyboardOpen;
    };
  }, [variant]);

  // Event rows are the machine annotating the conversation: a run of them neither opens a speaker block
  // nor interrupts one. The role label compares against the nearest turn that is not an event, and a
  // block's top gap is taken once, by the event run when it follows the person's message, so a reply
  // continuing after its event rows stays flush against them. Assistant steps are drawn one agent run
  // at a time (`transcriptBlocks`); the run after the person's newest message is live while a turn is in
  // flight or while its newest step is still streaming.
  // A shared conversation's human messages form author runs: a message continues the run when the block
  // drawn right before it is the same writer's message. Anything drawn between them (the agent's work, an
  // event row, a divider, an unattributed message) starts a new run, whose first message shows the avatar,
  // name and time. Only the block before a turn decides, so an appended turn never regroups earlier ones.
  let speaker: ChatTurn['role'] | undefined;
  let runWriter: number | undefined;
  const activeEnd = turns.length - (conversationAppendCount ?? 0);
  const lastYou = turns.findLastIndex((turn, index) => index < activeEnd && turn.role === 'you' && !isConversationOnlyTurn(turn));
  const working = !messageWindow && (busy || thinking);
  const blocks = transcriptBlocks(turns, working, messageWindow ? false : submittedRun?.awaitingEcho, messageWindow ? false : workPending, conversationAppendCount);
  const lastRun = blocks.findLastIndex(block => block.kind === 'run');
  const previousRunKeys = runKeysRef.current;
  const nextRunKeys = new Map<string, string>();
  // The first turn that actually renders owns the transcript's top edge: an ephemeral sibling above it (the
  // unread divider, the older-history sentinel, the desktop dock float) must never hand it a block gap.
  const firstRendered = blocks.findIndex(block => block.kind === 'run' || voiceCallProjection.get(block.index) !== null);
  const unreadDivider = useMemo(() => <div data-testid="chat-unread-divider" role="separator" className="my-4 flex items-center gap-2 text-meta text-muted-foreground"><span className="h-px flex-1 bg-border" aria-hidden />{t.brainChat.ux.unreadDivider}<span className="h-px flex-1 bg-border" aria-hidden /></div>, [t.brainChat.ux.unreadDivider]);
  const turnNodes: ReactNode[] = blocks.map((block, b) => {
    const first = block.kind === 'run' ? block.start : block.index;
    const firstTurn = block.kind === 'run' && block.start === block.end ? undefined : turns[first];
    const key = firstTurn?.id ?? `live:${first - firstLiveTurn}`;
    const previous = turns[first - 1]?.role;
    if (block.kind === 'turn') {
      // Coalesce only visible call messages; native work and append ownership keep their raw indices.
      const projectedCall = voiceCallProjection.get(block.index);
      if (projectedCall === null) return null;
      const turn = projectedCall && block.turn.role === 'you' ? { ...block.turn, ...projectedCall } : block.turn;
      const writer = turn.role === 'you' ? turn.author?.id : undefined;
      const continuesRun = writer !== undefined && writer === runWriter;
      runWriter = writer;
      const showRole = turn.role !== 'event' && speaker !== turn.role;
      // A new author run opens with the speaker break even when the previous message was a person's too. A run
      // of event markers is one group: it opens a block gap after whatever precedes it, its own rows stack flush,
      // and the turn after it opens a gap in turn.
      const breakBefore = turn.role === 'event' ? previous !== 'event' : showRole || previous === 'event' || (writer !== undefined && !continuesRun);
      if (turn.role !== 'event') speaker = turn.role;
      // `tk` is the block's reading-anchor identity (see `holdReadingAnchor`). A single turn's key already is.
      return <Fragment key={key}>{turn.id === unreadBoundaryId ? unreadDivider : null}<Message tk={key} turn={turn} models={models ?? undefined} showRole={showRole} breakBefore={breakBefore} first={b === firstRendered}
        readOnly={messageWindow !== null} viewerId={viewerId} reacting={reacting} people={people} quoteFor={quoteFor} continuesRun={continuesRun} phone={writer !== undefined && mobile === true}
        mentionUnseen={turn.role === 'you' && isUnseenPosition(turn.branchDepth, conversationReadState === null ? undefined : conversationReadState.lastReadDepth)} /></Fragment>;
    }
    runWriter = undefined;
    // Every agent run opens with its own head (avatar, name, time, duration), including a run that directly
    // follows another one, so consecutive runs read and align alike.
    speaker = 'elowen';
    let opener = block.start - 1;
    while (turns[opener] && (turns[opener]!.role === 'event' || isConversationOnlyTurn(turns[opener]!))) opener--;
    const opening = turns[opener];
    const submitted = !messageWindow && submittedRun && (submittedRun.awaitingEcho
      ? block.start === block.end
      : opener === lastYou && opening?.role === 'you' && opening.id === submittedRun.userId);
    const runTurns = turns.slice(block.start, block.end);
    const previousKey = runTurns.map(turn => turn.id === undefined ? undefined : previousRunKeys.get(turn.id))
      .find(value => value !== undefined);
    const runKey = previousKey ?? (submitted ? `send:${submittedRun.startedAt}`
      : opening?.role === 'you' ? `run:${opening.id ?? opener - firstLiveTurn}` : key);
    for (const turn of runTurns) if (turn.id !== undefined) nextRunKeys.set(turn.id, runKey);
    // The reading anchor names the run's last stored step, independent of its React key.
    const anchorId = turns[block.end - 1]?.id ?? runKey;
    return (
      <Fragment key={runKey}>
        {runTurns.some(turn => turn.id === unreadBoundaryId) ? unreadDivider : null}
        <AssistantRun
          tk={anchorId}
          turns={runTurns}
          models={models ?? undefined}
          showRole
          breakBefore
          first={b === firstRendered}
          showThoughts={showThoughts}
          live={!messageWindow && b === lastRun && block.start > lastYou && (working || workPending || submittedRun?.awaitingEcho === true || block.lastStep?.streaming === true)}
          outcome={runOutcome(turns[block.end])}
          startedAt={submitted ? submittedRun.startedAt : opening?.role === 'you' ? opening.createdAt : firstTurn?.role === 'elowen' ? firstTurn.createdAt : undefined}
          pendingPlanId={planDecision?.id}
          readOnly={messageWindow !== null}
          mentionReadDepth={conversationReadState === null ? undefined : conversationReadState.lastReadDepth}
          quoteFor={quoteFor}
          reacting={reacting}
          viewerId={viewerId}
          people={people}
          reveal={reveal && reveal.session === activeSessionId && runTurns.some((turn) => turn.id === reveal.id) ? reveal : undefined}
          phone={mobile === true}
        />
      </Fragment>
    );
  });
  // Keep only the rendered window, not a second history cache.
  runKeysRef.current = nextRunKeys;

  return (
    <div
      ref={surfaceRootRef}
      data-chat-keyboard-surface
      tabIndex={-1}
      className={`chat-surface relative flex flex-col ${variant === 'full' ? 'chat-surface-full min-h-0 flex-1 overflow-clip' : 'h-full min-h-0'}`}
      data-variant={variant}
    >
      <ChatConversationBar
        variant={variant}
        mobile={mobile}
        telemetryShown={telemetryShown}
        onOpenTelemetry={onOpenTelemetry}
        statuslineShown={statuslineShown}
        onSearch={openSearch}
      />
      {viewerId !== undefined && programStatus ? <div className={variant === 'full' ? 'chat-gutter' : 'px-3'}><SharedConversationStatus typing={typing} viewerId={viewerId} shared={!!membership?.participants.length} turnWriter={turnWriter} programStatus={programStatus} /></div> : null}
      {searchOpen && activeSessionId ? <ConversationSearch key={activeSessionId} session={activeSessionId} onClose={closeSearch} onJump={(id) => { void jumpToMessage(id, true).then((outcome) => { if (outcome === 'missing') toast(t.chat.messageUnavailable, 'error'); else if (outcome === 'failed') toast(t.brainChat.searchOpenError, 'error'); }); }} /> : null}
      {messageWindow ? <Button type="button" variant="ghost" onClick={returnToLatest}>{t.brainChat.ux.searchLatest}</Button> : null}
      {unreadBoundaryId && showUnreadJump ? <Button type="button" variant="ghost" onClick={() => { void jumpToMessage(unreadBoundaryId).then((outcome) => { if (outcome === 'missing') toast(t.chat.messageUnavailable, 'error'); else if (outcome === 'failed') toast(t.brainChat.searchOpenError, 'error'); }); }}>{t.brainChat.ux.jumpToUnread}</Button> : null}

      {/* Messages. Both variants have one explicit transcript scroller. The full page keeps its hero and
          composer outside that scroller; the inner content box is what grows and what late media resizes.
          Neither variant spaces turns with a container gap: each turn takes its own speaker break and
          segments carry symmetric spacing, as padding in desktop block flow so it cannot collapse. The
          rhythm between tools, events and prose is the same inside one turn and across a turn boundary, in the
          dock as on the full page. */}
      <div
        ref={scrollRef}
        data-testid="chat-transcript"
        // The transcript is the keyboard scroll target: a click anywhere in it focuses this scroller (not the
        // non-scrollable surface above it), so PageUp/PageDown, Home/End and the arrow keys page through the
        // history the way they do in any scroll container. `-1` keeps it out of the Tab order — the reader
        // reaches it by clicking, as they already did for the chat's own keymap.
        tabIndex={-1}
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain [overflow-anchor:none] ${variant === 'full' ? 'chat-transcript' : ''}`}
      >
      <div
        ref={transcriptContentRef}
        data-testid="chat-transcript-content"
        className={`flex min-h-full flex-col ${variant === 'full' ? 'chat-gutter chat-transcript-content' : 'p-3'}`}
      >
        {/* The empty start screen stays a centred flex column (chat.css): floats there would be flex items. */}
        {variant === 'full' && hasFloatingDock && blocks.length > 0 ? <>
          <div ref={dockPusherRef} className="chat-dock-pusher" aria-hidden />
          <div ref={dockFloatRef} className="chat-dock-float" aria-hidden />
        </> : null}
        {blocks.length === 0 ? (
          <div className={`chat-empty m-auto w-full ${ready ? 'max-w-2xl' : 'max-w-md'}`}>
            {ready ? <ChatStart variant={variant} recapSeed={recapSeed} /> : <LoadingState />}
          </div>
        ) : null}
        {/* Older-history sentinel: the oldest loaded point the prefetch band measures. A fixed-height slot
            kept whenever older history remains so mounting the spinner doesn't shift the transcript; the
            spinner shows while a page loads, the retry control after a page failed. */}
        {hasMoreHistory && !messageWindow && !searchOpen ? (
          <div ref={sentinelRef} data-testid="chat-history-sentinel" className="flex h-8 shrink-0 items-center justify-center gap-2" aria-hidden={olderState === 'idle'}>
            {olderState === 'loading' ? <Spinner size="md" label={t.brainChat.loadingOlder} /> : null}
            {olderState === 'failed' ? (
              <>
                <span role="status" className="min-w-0 truncate text-small text-muted-foreground">{t.brainChat.olderLoadError}</span>
                <Button type="button" variant="ghost" size="sm" onClick={retryOlder} aria-label={`${t.brainChat.olderLoadError} ${t.common.retry}`}>
                  {t.common.retry}
                </Button>
              </>
            ) : null}
          </div>
        ) : null}
        {/* Stable keys: history turns key by their store id, so a prepend never re-keys the existing turns;
            the live streaming tail (no id) keys by its offset within the live suffix, which is invariant
            under a prepend. */}
        {turnNodes}
        {!messageWindow ? <ProjectChatDock narration={narration} pendingInput={pendingInput} /> : null}
        {/* Input and modal extras stay after every turn: unlike ambient task/agent controls, these represent
            a decision the current turn is waiting for. `empty:hidden` drops the spacing group when every
            modal is portalled or closed. */}
        <div className={`chat-extras flex flex-col gap-3 empty:hidden ${variant === 'full' ? 'mt-4 text-small' : 'mt-3 text-small'}`}>
        {statsOpen ? (
          <StatsModal onClose={() => setStatsOpen(false)} />
        ) : null}
        {helpTab ? (
          <HelpModal key={helpTab} initialTab={helpTab} onClose={closeHelp} />
        ) : null}
        {planDecision && !liveTurns.some(turn => turn.role === 'elowen' && turn.segments.some(segment =>
          segment.kind === 'tools' && segment.items.some(item => item.id === planDecision.id && submittedPlanOf(item)))) ? (
          <PlanCard plan={planDecision.plan} id={planDecision.id} />
        ) : null}
        {ask && ask.questions.length > 0 ? (
          // Wrapped and focusable so `pendingInput.reveal` has something to bring the reader to: a plugin
          // dock view expanded over the dock hides this card, and the way back has to be the card itself.
          <div ref={askRef} tabIndex={-1} className="scroll-mt-4 focus:outline-none">
            <AskQuestionCard
              key={ask.id}
              questions={ask.questions}
              kind={ask.kind}
              readOnly={readOnly !== null || childFocus !== null}
              onSubmit={(answers) => onAnswer(ask.id, answers)}
              onReload={reloadAsk}
            />
          </div>
        ) : null}
        </div>
        {/* The pinned tail closes the column. Last of everything, so nothing in the transcript can be
            painted over by a tail stuck to the bottom of the scrollport — a pending question above all,
            which is a decision the turn is waiting for and must stay readable. */}
        <TranscriptAmbientExtras
          variant={variant}
          cards={ambientCards}
          live={busy}
          visible={ambientExtrasReady}
        />
        <div ref={transcriptEndRef} data-testid="chat-transcript-end" aria-hidden />
      </div>
      </div>

      <ChatFooterDock
        variant={variant}
        dockRef={composerDockRef}
        composerRef={composerRef}
        cards={ambientExtrasReady ? footerCards : []}
        live={busy}
        telemetryShown={telemetryShown}
        statuslineShown={statuslineShown}
        onToggleStatusline={toggleStatusline}
        onJumpToMessage={jumpFromFooter}
        keyboardStatus={keyboardStatus}
      />
    </div>
  );
}
